import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, Select, Tag, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { ABLE } from "@/meta/common";
import { USER_ORIGIN } from "@/meta/manageCenter/user";
import { confirmDialog } from "@/utils/confirmDialog";
import { useUserStore } from "@/views/stores";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import type { BackendUser, BackendUserPage, BackendUserQuery } from "../types";
import "./SystemBackendAccountPage.scss";

export interface SystemBackendAccountPageProps {
    deleteUser?: (id: number) => Promise<void>;
    loadUsers?: (query: BackendUserQuery) => Promise<BackendUserPage>;
    permissions?: string[];
    updateUser?: (id: number, user: BackendUser) => Promise<void>;
}

const defaultQuery: BackendUserQuery = { page: 1, size: 10 };

type BackendUserFilterValues = {
    origin?: number;
    param?: string;
};

/** 复用旧 Vue `/admin/user/paging` 接口的后台用户列表请求。 */
export async function loadBackendUsers(query: BackendUserQuery): Promise<BackendUserPage> {
    const response = await request.service.get<BackendUserPage>("/admin/user/paging", { params: query });
    return response.data;
}

/** 复用旧 Vue 删除接口，保留服务层可替换的测试边界。 */
export async function deleteBackendUser(id: number): Promise<void> {
    await request.service.delete(`/admin/user/${id}`);
}

/** 复用旧 Vue 更新接口，保留服务层可替换的测试边界。 */
export async function updateBackendUser(id: number, user: BackendUser): Promise<void> {
    await request.service.put(`/admin/user/${id}`, user);
}

/** 兼容 Vue `UserVo` 嵌套响应和迁移早期使用的扁平测试数据。 */
const getAccount = (record: BackendUser): Partial<BackendUser> => record.user || record;

const formatUserValue = (value: unknown): string =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);

/** Vue 列表对集团成员使用单手机号，对企业定制用户使用手机号数组。 */
const formatUserMobile = (record: BackendUser): string => {
    const account = getAccount(record);
    const mobiles = account.mobiles?.filter(Boolean).join(",");
    const mobile = account.origin === USER_ORIGIN.ZHIHAO.code ? account.mobile : mobiles || account.mobile;
    return formatUserValue(mobile);
};

/** 将 Vue 后台用户分页返回的 origin/state 枚举统一为列表文案和操作使用的数字。 */
function normalizeBackendUsers(rows?: BackendUser[]): BackendUser[] {
    return (rows || []).map(record => {
        const account = getAccount(record);
        const normalizedAccount: Partial<BackendUser> = {
            ...account,
            origin: account.origin == null ? undefined : Number(account.origin),
            state: account.state == null ? undefined : Number(account.state)
        };
        return record.user ? { ...record, user: normalizedAccount } : { ...record, ...normalizedAccount };
    });
}

/** 系统管理-后台用户页面，按模板目录实现首个真实 React 业务模块。 */
export function SystemBackendAccountPage({
    deleteUser = deleteBackendUser,
    loadUsers = loadBackendUsers,
    permissions,
    updateUser = updateBackendUser
}: SystemBackendAccountPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeBanButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    // 保留 Vue v-auth 的特殊权限：封禁/提示权限会保留写操作入口。
    const canWrite = currentPermissions.some(permission =>
        ["ROLE_USER_W", "ROLE_BUTTON_BAN", "ROLE_BUTTON_TIP"].includes(permission)
    );
    const writeActionsDisabled = permissions ? currentPermissions.includes("ROLE_BUTTON_BAN") : storeBanButton;
    const [users, setUsers] = useState<BackendUser[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const [query, setQuery] = useState<BackendUserQuery>(defaultQuery);
    const [filterForm] = Form.useForm<BackendUserFilterValues>();

    useEffect(() => {
        let disposed = false;
        setErrorMessage("");
        void loadUsers(query)
            .then(result => {
                if (!disposed) {
                    setUsers(normalizeBackendUsers(result.list));
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    // BDD-ADMIN-004：失败只影响当前列表，并清理可能残留的旧结果。
                    setUsers([]);
                    setTotal(0);
                    setErrorMessage("后台用户加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadUsers, query]);

    /** 写操作成功后重新读取当前分页，保持旧 Vue ProTable 的刷新语义。 */
    const reloadUsers = async () => {
        const result = await loadUsers(query);
        setUsers(normalizeBackendUsers(result.list));
        setTotal(result.total || 0);
    };

    const handleDelete = async (id: number | undefined) => {
        if (id == null) return;
        // 延续旧 Vue userService.delete 的确认文案，确认前不得触发删除请求。
        if (!(await confirmDialog("是否删除，删除后用户重新登录将重新注册账号"))) return;
        try {
            await deleteUser(id);
            await reloadUsers();
            setErrorMessage("");
            message.success("后台用户已删除");
        } catch {
            setErrorMessage("后台用户删除失败");
        }
    };

    const handleToggleState = async (user: BackendUser) => {
        const account = getAccount(user);
        if (account.id == null) return;
        // 旧 Vue userService.update 使用“是否保存”确认，启用和禁用均必须经过同一拦截。
        if (!(await confirmDialog("是否保存"))) return;
        try {
            await updateUser(account.id, {
                ...account,
                state: account.state === ABLE.ENABLE.code ? ABLE.DISABLE.code : ABLE.ENABLE.code
            });
            await reloadUsers();
            setErrorMessage("");
            message.success("后台用户状态已更新");
        } catch {
            setErrorMessage("后台用户状态更新失败");
        }
    };

    // 将旧 Vue ProTable 的关键词和用户来源筛选转换为分页接口参数。
    const submitFilters = ({ origin, param }: BackendUserFilterValues) => {
        const nextQuery: BackendUserQuery = { ...defaultQuery };
        if (param) nextQuery.param = param;
        if (origin) nextQuery.origin = Number(origin);
        setErrorMessage("");
        setQuery(nextQuery);
    };

    // 清空筛选条件并恢复旧 Vue 首屏分页参数。
    const resetFilters = () => {
        filterForm.resetFields();
        setQuery(defaultQuery);
    };

    // 用 zhihao-ui-react 列契约承接 Vue 后台用户的嵌套账号字段、权限操作和状态切换。
    const columns: TableColumnProps<BackendUser>[] = [
        {
            key: "employeeId",
            label: "工号",
            width: 180,
            prop: "employeeId",
            render: ({ row }) => formatUserValue(getAccount(row).employeeId)
        },
        {
            key: "name",
            label: "姓名",
            width: 180,
            prop: "name",
            render: ({ row }) => formatUserValue(getAccount(row).name)
        },
        {
            key: "origin",
            label: "类型",
            width: 148,
            prop: "origin",
            render: ({ row }) => formatUserValue(USER_ORIGIN.getIntroByCode(getAccount(row).origin))
        },
        { key: "mobile", label: "手机号", width: 180, prop: "mobile", render: ({ row }) => formatUserMobile(row) },
        {
            key: "mail",
            label: "邮箱",
            width: 180,
            prop: "mail",
            render: ({ row }) => formatUserValue(getAccount(row).mail)
        },
        {
            key: "roles",
            label: "授权角色",
            minWidth: 120,
            prop: "roles",
            render: ({ row }) => {
                const roles = getAccount(row).roles || row.roles || [];
                return roles.length ? (
                    <div className="flex flex-wrap gap-2">
                        {roles.map(role => (
                            <Tag color="blue" key={role.id ?? role.name}>
                                {role.name}
                            </Tag>
                        ))}
                    </div>
                ) : (
                    DEFAULT_VALUE
                );
            }
        },
        {
            key: "state",
            label: "状态",
            width: 100,
            prop: "state",
            render: ({ row }) => formatUserValue(ABLE.getIntroByCode(getAccount(row).state))
        },
        ...(canWrite
            ? [
                  {
                      key: "operation",
                      label: "操作",
                      fixed: "right" as const,
                      prop: "operation",
                      width: 240,
                      render: ({ row }: { row: BackendUser }) => (
                          <div className="flex gap-2">
                              <Button
                                  disabled={writeActionsDisabled}
                                  type="link"
                                  onClick={() =>
                                      void navigate(
                                          `/systemMangement/systemManage/backendAccount/edit?id=${getAccount(row).id || ""}`
                                      )
                                  }
                              >
                                  编辑
                              </Button>
                              <Button
                                  disabled={writeActionsDisabled}
                                  type="link"
                                  onClick={() => void handleDelete(getAccount(row).id)}
                              >
                                  删除
                              </Button>
                              <Button
                                  disabled={writeActionsDisabled}
                                  type="link"
                                  onClick={() => void handleToggleState(row)}
                              >
                                  {getAccount(row).state === ABLE.ENABLE.code ? "禁用" : "启用"}
                              </Button>
                          </div>
                      )
                  }
              ]
            : [])
    ];

    return (
        <section
            className="system-backend-account-page flex h-full min-h-full flex-col"
            aria-label="system-backend-account-page"
        >
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<BackendUser>
                    data={users}
                    columns={normalizeOperationColumns(columns, users)}
                    pagination={false}
                    rowKey={record =>
                        getAccount(record).id || getAccount(record).mobile || getAccount(record).name || "user"
                    }
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                后台用户
                            </Typography.Title>

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                {canWrite ? (
                                    <Button
                                        className="admin-table-primary-action"
                                        disabled={writeActionsDisabled}
                                        type="primary"
                                        onClick={() =>
                                            void navigate("/systemMangement/systemManage/backendAccount/add")
                                        }
                                    >
                                        新增
                                    </Button>
                                ) : null}
                                <Form.Item noStyle name="param">
                                    {/* 对齐旧 Vue ProTable 的搜索字段长度契约，避免提交超长查询条件。 */}
                                    <Input maxLength={32} placeholder="请输入姓名、手机号、工号或邮箱" />
                                </Form.Item>
                                <Form.Item noStyle name="origin">
                                    <Select
                                        allowClear
                                        aria-label="用户类型"
                                        options={USER_ORIGIN.options}
                                        placeholder="请选择类型"
                                    />
                                </Form.Item>
                                <Button
                                    aria-label="查询用户"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    onClick={resetFilters}
                                >
                                    重置
                                </Button>
                            </Form>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                // 沿用 Vue 分页参数，切换页大小时回到第 1 页。
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default SystemBackendAccountPage;
